/**
 * Counter Block - Interactivity API View Script
 *
 * Scroll-triggered counting animation with easeOutQuad easing.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

const easeOutQuad = ( t ) => t * ( 2 - t );

function formatNumber( value, decimals, useSeparator ) {
	const fixed = value.toFixed( decimals );
	if ( ! useSeparator ) {
		return fixed;
	}
	const [ int, dec ] = fixed.split( '.' );
	const formatted = int.replace( /\B(?=(\d{3})+(?!\d))/g, ',' );
	return dec !== undefined ? `${ formatted }.${ dec }` : formatted;
}

function startAnimation( ctx ) {
	const start = ctx.startValue;
	const end = ctx.endValue;
	const duration = ctx.duration;
	const startTime = performance.now();

	function tick( now ) {
		const elapsed = now - startTime;
		const progress = Math.min( elapsed / duration, 1 );
		const eased = easeOutQuad( progress );
		ctx._currentValue = start + ( end - start ) * eased;

		if ( progress < 1 ) {
			ctx._rafHandle = requestAnimationFrame( tick );
		} else {
			ctx._currentValue = end;
		}
	}

	ctx._rafHandle = requestAnimationFrame( tick );
}

store( 'brandy/counter', {
	state: {
		get displayValue() {
			const ctx = getContext();
			const current = ctx._currentValue ?? ctx.endValue;
			return formatNumber(
				current,
				ctx.decimals,
				ctx.useThousandSeparator
			);
		},
		get circleOffset() {
			const ctx = getContext();
			if ( ctx.type !== 'circle' ) {
				return '0';
			}
			const current = ctx._currentValue ?? ctx.endValue;
			const radius = ( 100 - ctx.strokeWidth ) / 2;
			const circumference = 2 * Math.PI * radius;
			const progress = Math.min( current / ( ctx.maxValue || 1 ), 1 );
			return String( circumference * ( 1 - progress ) );
		},
	},
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			// Set initial display to startValue (override SSR endValue).
			ctx._currentValue = ctx.startValue;
			ctx._animated = false;

			const observer = new IntersectionObserver(
				( entries ) => {
					if ( entries[ 0 ].isIntersecting && ! ctx._animated ) {
						ctx._animated = true;
						observer.disconnect();
						startAnimation( ctx );
					}
				},
				{ threshold: 0.2 }
			);

			observer.observe( ref );

			return () => {
				observer.disconnect();
				cancelAnimationFrame( ctx._rafHandle );
			};
		},
	},
} );
